import React, { FC, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { homeRoutesURLs } from '../../constants/routes';
import Page404 from '../../pagesAdmin/Page404/Page404';
import AdminLayout from '../../pagesAdmin/Layout';
import MainLayout from '../../pagesMain/Layout/MainLayout';
import TerminalPage from '../../pagesMain/TerminalPage/TerminalPage';
import TerminalPreviewPage from '../../pagesMain/TerminalPreviewPage/TerminalPreviewPage';
import QueuePage from '../../pagesMain/QueuePage/QueuePage';
import RatingPage from '../../pagesMain/RatingPage/RatingPage';
import LoginPage from '../../pagesMain/LoginPage/LoginPage';
import SentryTestPage from '../../pagesMain/SentryTestPage/SentryTestPage';
import { ProtectedRoute } from '../ProtectedRoute/ProtectedRoute';
import { AlertContext } from '../../context/AlertProvider';
import Alert from '../Alert/Alert';
import AxiosInterceptor from '../Interceptor/AxiosInterceptor';
import { AlertType } from '../../types/enums';
import { LicenseProvider } from '../../context/LisenceContext/LicenseProvider';
import { defaultErrorText, ErrorResponseType } from '../../types/Error/ErrorResponseType';

const HomeRoutes: FC = () => {
  const { loginPagePath, mainPagePath, adminPagePath, terminalPagePath, queuePagePath, ratingPagePath, sentryTestPagePath } =
    homeRoutesURLs;

  const [alert, setAlert] = useState<
    | { type: AlertType; message: string }
    | ErrorResponseType
    | { type?: string; message?: string }
    | string
    | null
  >(null);
  const isLogin = localStorage.getItem('user') !== null ? true : false;
  const [isAuthenticated, setIsAuthenticated] = useState(isLogin);

  const normalizedAlert = React.useMemo(() => {
    if (!alert) return null;
    if (typeof alert === 'string') {
      const msg = alert.trim();
      return { type: AlertType.Error, message: msg || defaultErrorText };
    }
    const rawMessage = String((alert as any).message ?? '').trim();
    const rawType = (alert as any).type;
    const type =
      rawType === AlertType.Success
        ? AlertType.Success
        : rawType === AlertType.Error
          ? AlertType.Error
          : AlertType.Error;
    return { type, message: rawMessage || defaultErrorText };
  }, [alert]);

  return (
    <AlertContext.Provider value={{ setAlert }}>
      <LicenseProvider>
        <>
          {normalizedAlert && (
            <Alert
              message={normalizedAlert.message}
              autoHideDuration={4500}
              onClose={() => setAlert(null)}
              type={normalizedAlert.type}
            />
          )}

          <AxiosInterceptor setIsAuthenticated={setIsAuthenticated} />

          <Routes>
            <Route
              path={loginPagePath}
              element={
                <LoginPage
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                />
              }
            />
            <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
              <Route
                path={mainPagePath}
                element={<MainLayout setIsAuthenticated={setIsAuthenticated} />}
              />
              <Route
                path={adminPagePath}
                element={<AdminLayout setIsAuthenticated={setIsAuthenticated} />}
              />
            </Route>
            <Route path={terminalPagePath} element={<TerminalPage />} />
            <Route path="terminal-preview" element={<TerminalPreviewPage />} />
            <Route path={queuePagePath} element={<QueuePage />} />
            <Route path={ratingPagePath} element={<RatingPage />} />
            <Route path={sentryTestPagePath} element={<SentryTestPage />} />
            <Route path="/*" element={<Page404 />} />
          </Routes>
        </>
      </LicenseProvider>
    </AlertContext.Provider>
  );
};

export default HomeRoutes;
